import React, { useMemo, useState } from "react";
import { Card, DatePicker, Space, Table, Button, Select, Statistic, Row, Col, Alert } from "antd";
import { BarChartOutlined } from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface VatSummaryDto {
    currency: string;
    totalWithVat: number;
    totalWithoutVat: number;
    vatTotal: number;
}

interface VatReportDto {
    fromDate: string;
    toDate: string;
    sales: VatSummaryDto[];
    purchases: VatSummaryDto[];
    netVatPerCurrency: VatSummaryDto[];
}

const currencyOptions = [
    { label: "All", value: "" },
    { label: "USD", value: "USD" },
    { label: "ZWL", value: "ZWL" },
    { label: "EUR", value: "EUR" },
];

export const VatReport: React.FC = () => {
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf("month"),
        dayjs().endOf("month"),
    ]);
    const [currency, setCurrency] = useState<string>("");

    const { data, isLoading, refetch, error } = useCustom({
        url: "reports/vat",
        method: "get",
        config: {
            query: {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                currency: currency || undefined,
            },
        },
        queryOptions: {
            enabled: false,
        },
    });

    const report = (data?.data?.data || {}) as VatReportDto;

    const totals = useMemo(() => {
        const sum = (items: VatSummaryDto[] = []) =>
            items.reduce(
                (acc, item) => {
                    acc.totalWithVat += item.totalWithVat;
                    acc.totalWithoutVat += item.totalWithoutVat;
                    acc.vatTotal += item.vatTotal;
                    return acc;
                },
                { totalWithVat: 0, totalWithoutVat: 0, vatTotal: 0 }
            );

        return {
            sales: sum(report.sales || []),
            purchases: sum(report.purchases || []),
            net: sum(report.netVatPerCurrency || []),
        };
    }, [report]);

    const columns = [
        {
            title: "Currency",
            dataIndex: "currency",
            key: "currency",
            width: 120,
        },
        {
            title: "Total With VAT",
            dataIndex: "totalWithVat",
            key: "totalWithVat",
            align: "right" as const,
            render: (value: number) => value.toLocaleString(),
        },
        {
            title: "Total Without VAT",
            dataIndex: "totalWithoutVat",
            key: "totalWithoutVat",
            align: "right" as const,
            render: (value: number) => value.toLocaleString(),
        },
        {
            title: "VAT Total",
            dataIndex: "vatTotal",
            key: "vatTotal",
            align: "right" as const,
            render: (value: number) => value.toLocaleString(),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <BarChartOutlined />
                    VAT Report
                </Space>
            }
            extra={
                <Space>
                    <RangePicker
                        value={dateRange}
                        onChange={(range) => {
                            if (range && range[0] && range[1]) {
                                setDateRange([range[0], range[1]]);
                            }
                        }}
                        format="DD/MM/YYYY"
                    />
                    <Select
                        value={currency}
                        onChange={setCurrency}
                        options={currencyOptions}
                        style={{ width: 140 }}
                    />
                    <Button type="primary" onClick={() => refetch()} loading={isLoading}>
                        Generate Report
                    </Button>
                </Space>
            }
        >
            {error && (
                <Alert
                    type="error"
                    message="Failed to load VAT report"
                    description={(error as Error).message}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={8}>
                    <Statistic title="Sales VAT" value={totals.sales.vatTotal} />
                </Col>
                <Col xs={24} md={8}>
                    <Statistic title="Purchases VAT" value={totals.purchases.vatTotal} />
                </Col>
                <Col xs={24} md={8}>
                    <Statistic title="Net VAT" value={totals.net.vatTotal} />
                </Col>
            </Row>

            <Card type="inner" title="Sales with VAT" style={{ marginBottom: 16 }}>
                <Table
                    rowKey={(record) => `sales-${record.currency}`}
                    columns={columns}
                    dataSource={report.sales || []}
                    pagination={false}
                    loading={isLoading}
                />
            </Card>

            <Card type="inner" title="Purchases with VAT" style={{ marginBottom: 16 }}>
                <Table
                    rowKey={(record) => `purchases-${record.currency}`}
                    columns={columns}
                    dataSource={report.purchases || []}
                    pagination={false}
                    loading={isLoading}
                />
            </Card>

            <Card type="inner" title="Net VAT (Sales minus Purchases)">
                <Table
                    rowKey={(record) => `net-${record.currency}`}
                    columns={columns}
                    dataSource={report.netVatPerCurrency || []}
                    pagination={false}
                    loading={isLoading}
                />
            </Card>
        </Card>
    );
};
